import {
  AdvancedSettings,
  AnalyticsPageName,
  BotType,
  OnlyHelperTooltipProps,
  OrderTypes,
} from "@shared/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  BotFormValues,
  ParsedDcaStrategyData,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  ALTERNATIVE_USD_PAIRS_TITLE,
  CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  DCA_STRATEGY_KEY_TO_TITLE,
  OPERATION_HOURS_TITLE,
  PRICE_ADJUSTMENT_BASELINE_TITLE,
  STOP_BASELINE_TITLE,
  STOP_PERCENT_TITLE,
  STOP_TIMEOUT_TITLE,
  STRATEGY_KEYS,
  TAKE_PROFIT_BASELINE_TITLE,
  TAKE_PROFIT_GRACE_TITLE,
  TAKE_PROFIT_PERCENT_TITLE,
} from "@shared/features/bots/BotForm/common/constants";
import { getMaxPriceDifferenceHelperData } from "@shared/features/bots/BotForm/common/utils";
import { FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types/common/common.types";

const useFormHelperData = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType, isMarginExchange, isChannelConfiguration } =
    useContext(BotFormContext) || {};
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;

  const getTrailingHelperData = (fieldName: HelperFieldType) => {
    if (fieldName === ToggleField.TRAILING_STOP) {
      return {
        analyticsFieldName: `${
          isSignalsBot && isMarginExchange ? "Leveraged " : ""
        }Trailing Stop`,
        description: t("helper_tooltips.trailing_stop.description"),
        analyticsPageName: AnalyticsPageName.BOT_FORM,
        articleId: "5814874",
      };
    }
    const isEntry = fieldName === ToggleField.TRAILING_ENTRY;
    const helperDataDescription = [
      t("helper_tooltips.trailing.description_base"),
    ];
    if (isEntry && !isSignalsBot && !isGridBot) {
      helperDataDescription.push(
        t("helper_tooltips.trailing.entry_note_first_order_market")
      );
    }
    return {
      analyticsFieldName: `Trailing ${isEntry ? "Entry" : "Take-Profit"}`,
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleLink: "https://intercom.help/cornix/en/articles/5814927-trailing",
      description: helperDataDescription,
    };
  };

  const getStrategyItemHelperData = (
    strategyKey: keyof ParsedDcaStrategyData,
    orderType?: OrderTypes
  ) => {
    const isEntry = orderType === OrderTypes.ENTRY;
    const objectName = isEntry ? "entry" : "take-profit";
    let description: string | string[] | undefined = undefined;
    if (strategyKey === "numOrders") {
      description = t(`strategy_helper.num_orders.description`, { objectName });
    } else if (strategyKey === "firstOrderPercent") {
      description = t(`strategy_helper.first_order_percent.description`, {
        objectName,
      });
    } else if (strategyKey === "numActiveOrders") {
      description = t(`strategy_helper.num_active_orders.description`, {
        objectName,
      });
    } else if (strategyKey === "amountScale") {
      description = [
        t(`strategy_helper.amount_scale.description_line1`, { objectName }),
        t(`strategy_helper.amount_scale.example`),
      ];
    } else if (strategyKey === "priceDifference") {
      description = [
        t(`strategy_helper.price_difference.description_line1`, { objectName }),
      ];
      if (isEntry) {
        description.push(t(`strategy_helper.price_difference.example_entry`));
      } else {
        description.push(
          t(`strategy_helper.price_difference.example_take_profit`)
        );
      }
    } else if (strategyKey === "priceScale") {
      description = [
        t(`strategy_helper.price_scale.description_line1`, { objectName }),
      ];
      if (isEntry) {
        description.push(t(`strategy_helper.price_scale.example_entry`));
      } else {
        description.push(t(`strategy_helper.price_scale.example_take_profit`));
      }
    }
    return {
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      analyticsFieldName: t(DCA_STRATEGY_KEY_TO_TITLE[strategyKey]),
      description,
      articleId: "8976601",
    };
  };

  const getEntryGraceHelperData = () => {
    return {
      analyticsFieldName: t(FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE),
      description: [
        t("helper_tooltips.entry_grace.line1"),
        t("helper_tooltips.entry_grace.line2"),
        t("helper_tooltips.entry_grace.line3"),
      ],
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976601",
    };
  };

  const getMovingTakeProfitHelperData = () => {
    return {
      analyticsFieldName: "Moving Take-Profits",
      description: t("helper_tooltips.moving_take_profits.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976604",
    };
  };

  const getTakeProfitBaselineHelperData = () => {
    return {
      analyticsFieldName: isSignalsBot
        ? t(PRICE_ADJUSTMENT_BASELINE_TITLE)
        : t(TAKE_PROFIT_BASELINE_TITLE),
      description: isSignalsBot
        ? [
            t("helper_tooltips.take_profit_baseline.signals.line1"),
            t("helper_tooltips.take_profit_baseline.signals.line2"),
            t("helper_tooltips.take_profit_baseline.signals.line3"),
          ]
        : [
            t("helper_tooltips.take_profit_baseline.dca.line1"),
            t("helper_tooltips.take_profit_baseline.dca.line2"),
          ],
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    };
  };

  const getLongShortHelperData = () => {
    const description =
      botType === BotType.SIGNAL
        ? t("helper_tooltips.direction.signals")
        : t("helper_tooltips.direction.generic");
    return {
      analyticsFieldName: "Direction",
      description,
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8975691",
    };
  };

  const FORM_KEY_TO_HELPER_DATA: {
    [key in HelperFieldType]?: OnlyHelperTooltipProps;
  } = {
    multiSymbolMarginData: {
      analyticsFieldName: t("labels.leverage"),
      description: t(
        "helper_tooltips.form.multi_symbol_margin_data.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8975691",
    },
    symbols: {
      analyticsFieldName: t("labels.symbols"),
      description: t("helper_tooltips.form.symbols.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8975691",
    },
    [ToggleField.AMOUNT_PER_TRADE]: {
      analyticsFieldName: t("labels.amount_per_trade"),
      description: t("helper_tooltips.form.amount_per_trade.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "5814867",
    },
    cooldown_after_trade_close: {
      analyticsFieldName: t("advanced.cooldown_after_trade_close"),
      description: t(
        "helper_tooltips.form.cooldown_after_trade_close.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    cooldown_between_opening_trades_per_symbol: {
      analyticsFieldName: t(
        "advanced.cooldown_between_opening_trades_per_symbol"
      ),
      description: t(
        "helper_tooltips.form.cooldown_between_opening_trades_per_symbol.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    cooldown_between_opening_trades: {
      analyticsFieldName: t("advanced.cooldown_between_opening_trades"),
      description: t(
        "helper_tooltips.form.cooldown_between_opening_trades.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    number_of_trades_till_bot_stop: {
      analyticsFieldName: t("advanced.number_of_trades_till_bot_stop"),
      description: t(
        "helper_tooltips.form.number_of_trades_till_bot_stop.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    number_of_simultaneous_trades: {
      analyticsFieldName: t("advanced.number_of_simultaneous_trades"),
      description: t(
        "helper_tooltips.form.number_of_simultaneous_trades.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    min_symbol_price: {
      analyticsFieldName: t("advanced.min_symbol_price_usd"),
      description: t("helper_tooltips.form.min_symbol_price.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    min_symbol_volume: {
      analyticsFieldName: t("advanced.min_symbol_24h_volume_usd"),
      description: t("helper_tooltips.form.min_symbol_volume.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    concurrent_max_usd_invested: {
      analyticsFieldName: t("advanced.max_concurrent_amount_usd"),
      description: t(
        "helper_tooltips.form.concurrent_max_usd_invested.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    number_of_simultaneous_trades_per_symbol: {
      analyticsFieldName: t(
        "advanced.number_of_simultaneous_trades_per_symbol"
      ),
      description: t(
        "helper_tooltips.form.number_of_simultaneous_trades_per_symbol.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    auto_cancel_timeout_seconds: {
      analyticsFieldName: "advanced.auto_cancel_timeout_seconds",
      description: t(
        "helper_tooltips.form.auto_cancel_timeout_seconds.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    stopType: {
      analyticsFieldName: t("labels.stop_type"),
      description: t("helper_tooltips.form.stop_type.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8694613",
    },
    numberOfContinuousStopsTillBotStop: {
      analyticsFieldName: t("labels.number_of_continuous_stops"),
      description: t(
        "helper_tooltips.form.number_of_continuous_stops_till_bot_stop.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    stopPercent: {
      analyticsFieldName: t(STOP_PERCENT_TITLE),
      description: t("helper_tooltips.form.stop_percent.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    stopBaseline: {
      analyticsFieldName: t(STOP_BASELINE_TITLE),
      description: [
        t("helper_tooltips.form.stop_baseline.line1"),
        t("helper_tooltips.form.stop_baseline.line2"),
      ],
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "5814860",
    },
    stopTimeout: {
      analyticsFieldName: t(STOP_TIMEOUT_TITLE),
      description: t("helper_tooltips.form.stop_timeout.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    takeProfitPercent: {
      analyticsFieldName: t(TAKE_PROFIT_PERCENT_TITLE),
      description: t("helper_tooltips.form.take_profit_percent.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    mostTradedPairs: {
      analyticsFieldName: t("helper_titles.most_traded_pairs"),
      description: [
        t("helper_tooltips.form.most_traded_pairs.line1"),
        t("helper_tooltips.form.most_traded_pairs.line2"),
      ],
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    [ToggleField.ENTRY_PRICE_STRATEGY]: {
      analyticsFieldName: t("strategy.entries_prices"),
      description: t("helper_tooltips.form.entry_price_strategy.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976601",
    },
    [ToggleField.ENTRY_RATIOS]: {
      analyticsFieldName: t("labels.entries_ratios"),
      description: t("helper_tooltips.form.entry_ratios.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976601",
    },
    [ToggleField.TAKE_PROFIT_RATIOS]: {
      analyticsFieldName: t("labels.take_profits_ratios"),
      description: t("helper_tooltips.form.take_profit_ratios.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976604",
    },
    [ToggleField.TAKE_PROFIT_PRICE_STRATEGY]: {
      analyticsFieldName: t("strategy.take_profits_prices"),
      description: t(
        "helper_tooltips.form.take_profit_price_strategy.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976604",
    },
    [ToggleField.TAKE_PROFITS]: {
      analyticsFieldName: t("labels.first_take_profit_distance"),
      description: t("helper_tooltips.form.take_profits.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "8976604",
    },
    [ToggleField.ALTERNATIVE_USD_PAIRS]: {
      analyticsFieldName: t(ALTERNATIVE_USD_PAIRS_TITLE),
      description: t("helper_tooltips.form.alternative_usd_pairs.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "10792610",
    },
    originalLeverageModifiedWarning: {
      analyticsFieldName: t("helper_titles.original_leverage_modified_warning"),
      description: [
        t("helper_tooltips.form.original_leverage_modified_warning.line1"),
        t("helper_tooltips.form.original_leverage_modified_warning.line2"),
        t("helper_tooltips.form.original_leverage_modified_warning.line3"),
      ],
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    signalsBotSymbolModifiedWarning: {
      analyticsFieldName: t(
        "helper_titles.signals_bot_symbol_modified_warning"
      ),
      description: t(
        "helper_tooltips.form.signals_bot_symbol_modified_warning.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
    },
    useAsFallbackOnly: {
      analyticsFieldName: t("helper_titles.use_as_fallback_only"),
      description: t("helper_tooltips.form.use_as_fallback_only.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "9274477",
    },
    [ToggleField.TAKE_PROFIT_GRACE_PERCENT]: {
      analyticsFieldName: t(TAKE_PROFIT_GRACE_TITLE),
      description: t(
        "helper_tooltips.form.take_profit_grace_percent.description"
      ),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "11121738",
    },
    [ToggleField.OPERATION_HOURS]: {
      analyticsFieldName: t(OPERATION_HOURS_TITLE),
      description: t("helper_tooltips.form.operation_hours.description"),
      analyticsPageName: AnalyticsPageName.BOT_FORM,
      articleId: "11515744",
    },
  } as const;

  const getHelperData = (
    fieldName?: HelperFieldType,
    orderType?: OrderTypes
  ): OnlyHelperTooltipProps | undefined => {
    if (!fieldName) return;
    if (fieldName in FORM_KEY_TO_HELPER_DATA) {
      if (fieldName === ToggleField.TAKE_PROFITS && !isSignalsBot) return;
      return FORM_KEY_TO_HELPER_DATA[
        fieldName as keyof typeof FORM_KEY_TO_HELPER_DATA
      ];
    } else if (STRATEGY_KEYS_SET.has(fieldName as any)) {
      return getStrategyItemHelperData(fieldName as any, orderType);
    } else if (
      fieldName === ToggleField.TRAILING_ENTRY ||
      fieldName === ToggleField.TRAILING_TAKE_PROFIT ||
      fieldName === ToggleField.TRAILING_STOP
    ) {
      return getTrailingHelperData(fieldName);
    } else if (fieldName === "maxPriceDifference") {
      return getMaxPriceDifferenceHelperData(botType, t);
    } else if (fieldName === ToggleField.CLOSE_ON_TP_BEFORE_ENTRY) {
      return {
        analyticsFieldName: t(CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE),
        description: t("descriptions.close_trade_on_tp_before_entry"),
        analyticsPageName: AnalyticsPageName.BOT_FORM,
        articleId: isChannelConfiguration ? "5814956" : "5814872",
      };
    } else if (fieldName === ToggleField.ENTRY_GRACE_PERCENT) {
      return getEntryGraceHelperData();
    } else if (fieldName === ToggleField.TAKE_PROFIT_DYNAMIC) {
      return getMovingTakeProfitHelperData();
    } else if (fieldName === "takeProfitBaseline") {
      return getTakeProfitBaselineHelperData();
    } else if (fieldName === "longShort") {
      return getLongShortHelperData();
    }
  };

  return {
    getHelperData,
  };
};

export default useFormHelperData;

const STRATEGY_KEYS_SET = new Set(STRATEGY_KEYS);

type HelperFieldType =
  | keyof BotFormValues
  | keyof AdvancedSettings
  | keyof ParsedDcaStrategyData
  | ToggleField
  | "maxPriceDifference"
  | "takeProfitPercent"
  | "takeProfitBaseline"
  | "stopPercent"
  | "stopBaseline"
  | "mostTradedPairs"
  | "originalLeverageModifiedWarning"
  | "signalsBotSymbolModifiedWarning"
  | "useAsFallbackOnly";
